"use client";

import { ReactNode, useEffect } from "react";
import Image from "next/image";

import ForwardIcon from "@/assets/icons/forward-15.svg";
import PauseIcon from "@/assets/icons/pause.svg";
import PlayIcon from "@/assets/icons/play.svg";
import BackwardIcon from "@/assets/icons/replay-5.svg";
import { AudioSlider } from "@/components/partials";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AudioInfoType, useAudioContext, useCart } from "@/context";
import { INTRO_LOG_KEY } from "@/lib/constants";
import { ID } from "@/lib/schema";
import { apiImage, currencyFormat, removeHTML, storage } from "@/lib/utils";

export function IntroDialog({
  title,
  audio,
  body,
  price,
  productId,
  lecturesDuration,
  children,
}: {
  audio: AudioInfoType;
  body: string;
  title: string;
  price: number;
  lecturesDuration: number;
  productId: ID;
  children: ReactNode;
}) {
  const { addToCart, items } = useCart();
  const currentItem = items.find((c) => c.productId === productId);

  const {
    isPlaying,
    currentTime,
    duration,
    togglePlayPause,
    handleForward,
    handleBackward,
    toggleAudioDrawer,
    setAudio,
    formatTime,
  } = useAudioContext();

  useEffect(() => {
    if (
      !(storage.get<AudioInfoType["id"][]>(INTRO_LOG_KEY) || []).includes(
        audio.id
      )
    )
      setAudio(audio, false);
  }, [audio]);

  const saveClosed = () =>
    storage.set<AudioInfoType["id"][]>(INTRO_LOG_KEY, (oldValues, setter) =>
      setter([...(oldValues || []), audio.id])
    );

  return (
    <Dialog
      defaultOpen={
        !(storage.get<AudioInfoType["id"][]>(INTRO_LOG_KEY) || []).includes(
          audio.id
        )
      }
      onOpenChange={(o) => {
        toggleAudioDrawer(true);
        saveClosed();
        if (o) setAudio(audio, false);
      }}
    >
      <DialogTrigger asChild>
        <button type="button">{children}</button>
      </DialogTrigger>
      <DialogContent className="w-11/12 sm:max-w-[530px] rounded-xl sm:rounded-3xl pt-14 max-h-[95%] overflow-y-auto">
        <DialogTitle>
          <figure
            itemProp="image"
            itemScope
            itemType="https://schema.org/ImageObject"
            className="rounded-xl w-1/2 md:w-4/5 overflow-hidden aspect-square relative mx-auto mb-10 md:mb-14 shadow-lg max-w-[247px]"
          >
            <Image
              src={apiImage(audio.thumbnail)}
              fill
              alt={title}
              itemProp="url"
              className="object-cover"
            />
            <meta itemProp="width" content="128" />
            <meta itemProp="height" content="128" />
          </figure>
          {title}
        </DialogTitle>
        <div className="text-sm">
          <p className="mb-3 line-clamp-3 md:line-clamp-4 text-[#898480]">
            {removeHTML(body)}
          </p>
          <div className="flex items-center gap-5 flex-wrap max-md:gap-2 max-md:justify-between">
            {/* <p>
              <span className="text-[#84807D]">Үргэлжлэх хугацаа: </span>
              <span>{formatTime(lecturesDuration)} мин</span>
            </p> */}
            <p>
              <span className="text-[#84807D]">Үнэ: </span>
              <span>{currencyFormat(price)}</span>
            </p>
          </div>
          <div className="my-10 md:my-16">
            <AudioSlider size="lg" />
            <div className="flex justify-between text-sm text-[#898480] mt-1 mb-2">
              <span suppressHydrationWarning>{formatTime(currentTime)}</span>
              <span suppressHydrationWarning>
                {currentTime >= duration ? "" : "-"}
                {formatTime((duration || 0) - currentTime)}
              </span>
            </div>

            <div className="flex items-center gap-6 justify-center mb-8">
              <button
                type="button"
                className="transition active:-rotate-45 active:scale-95"
                onClick={handleBackward}
              >
                <BackwardIcon
                  style={{ width: 32, height: 32, color: "#84807D" }}
                />
              </button>
              <button
                type="button"
                className="transition active:scale-75 w-16 h-16 bg-primary flex items-center justify-center rounded-full"
                onClick={togglePlayPause}
              >
                {isPlaying ? (
                  <PauseIcon style={{ width: 22, height: 22, color: "#fff" }} />
                ) : (
                  <PlayIcon style={{ width: 18, height: 18, color: "#fff" }} />
                )}
              </button>
              <button
                type="button"
                className="transition active:rotate-45 active:scale-95"
                onClick={handleForward}
              >
                <ForwardIcon
                  style={{ width: 32, height: 32, color: "#84807D" }}
                />
              </button>
            </div>
          </div>
          <Button
            type="button"
            variant="default"
            className="w-full font-bold text-base !py-3 h-12 rounded-xl"
            disabled={!!currentItem}
            suppressHydrationWarning
            onClick={() =>
              addToCart({
                price,
                productId,
                productName: title,
                productType: 1,
                quantity: 1,
                total: price,
              })
            }
          >
            {currentItem ? "Сагсалсан" : "Худалдаж авах"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
